import React, { useState, useEffect } from 'react'
import { Tag, Table, Button, Tooltip, Modal, Input, Select, message, Typography } from 'antd';
import { Helmet } from "react-helmet";
import axios from 'axios';
import { useForm, Controller, set } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import dayjs from 'dayjs';
import { EditOutlined } from '@ant-design/icons';
import RequirePermission from "../components/Common/RequirePermission";

const { Title } = Typography;

const schema = yup.object().shape({
    version_number: yup.string().required("Version is required"),
    is_force_update: yup.string().required("Force update is required"),
});

const AndroidAppVersions = () => {
    const [loading, setLoading] = useState(false);
    const [details, setDetails] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [editPermission, setEditPermission] = useState(false);
    const [createPermission, setCreatePermission] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [newVersion, setNewVersion] = useState("");
    const [newIsForceUpdate, setNewIsForceUpdate] = useState("no");

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        const permissions = userData.permissions || {};
        setEditPermission(permissions["signage.android_app_version_edit"] === "1");
        setCreatePermission(permissions["signage.android_app_version_create"] === "1");
    }, []);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            is_force_update: "no",
        },
    });

    const isForceUpdate = [
        {
            value: "no",
            label: "No",
        },
        {
            value: "yes",
            label: "Yes",
        },
    ]

    const handleTableChange = (pagination, filters, sorter) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    const handleEdit = (record) => {
        reset(record);
        setEditId(record.id);
        setIsModalOpen(true);
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                process.env.REACT_APP_BASE_URL + `/api/android-app-versions`,
                {
                    params: {
                        limit: pageSize,
                        page: currentPage,
                    },
                }
            )
            const record = response.data;
            setDetails(record);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.error("Error fetching Android app version data", error);
        }
    };

    useEffect(() => {
        const fetchTimer = setTimeout(() => {
            fetchData();
        }, 500);

        return () => clearTimeout(fetchTimer);
    }, [currentPage, pageSize]);

    const onUpdateForm = async (data) => {
        try {
            setLoading(true);
            const response = await axios.put(
                process.env.REACT_APP_BASE_URL + `/api/android-app-versions/update`,
                {
                    id: editId,
                    version_number: data.version_number,
                    is_force_update: data.is_force_update
                }
            );
            if (response.status === 200) {
                message.success("Android app version updated successfully");
            }
            await fetchData();
            setLoading(false);
            setIsModalOpen(false);
        } catch (error) {
            console.error("Error updating Android app version data", error);
        }
    };

    const handleAdd = async () => {
        if (!newVersion.trim()) return;
        try {
            setLoading(true);
            await axios.post(
                process.env.REACT_APP_BASE_URL + `/api/android-app-versions`,
                {
                    version_number: newVersion.trim(),
                    is_force_update: newIsForceUpdate === "yes",
                }
            );
            message.success("Android app version added successfully");
            setAddModalOpen(false);
            setNewVersion("");
            setNewIsForceUpdate("no");
            setCurrentPage(1);
            await fetchData();
        } catch (error) {
            console.error("Error adding Android app version", error);
            message.error("Failed to add version");
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Version Number',
            dataIndex: 'version_number',
            key: 'version_number',
            render: (text) => (
                <span>{text}</span>
            )
        },
        {
            title: 'Force Update',
            dataIndex: 'is_force_update',
            key: 'update',
            render: (text) => (
                <Tag color={text === "yes" ? 'green' : 'red'}>
                    {text === "yes" ? "Yes" : "No"}
                </Tag>
            )
        },
        {
            title: 'Modified By',
            dataIndex: 'modified_by',
            key: 'modified_by',
            render: (text) => (
                <span>{text}</span>
            )
        },
        {
            title: 'Modified At',
            dataIndex: 'modified_at',
            key: 'modified_at',
            render: (text) => (
                <span className="text-small">{text ? dayjs(text).format('DD-MM-YYYY HH:mm') : ""}</span>
            )
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                editPermission && (
                    <Tooltip title="Update">
                        <Button
                            icon={<EditOutlined />}
                            size="small"
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                )
            )
        }
    ]
    return (
        <RequirePermission permission="signage.android_app_version_view">
        <div style={{ padding: '1rem' }} >
            <Helmet>
                <title>{`${process.env.REACT_APP_NAME} | Android TV App`}</title>
            </Helmet>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Title level={3} style={{ marginBottom: '1rem' }}>
                    Android TV App
                </Title>
                <Button
                    type="primary"
                    onClick={() => setAddModalOpen(true)}
                    disabled={!createPermission}
                >
                    Add Version
                </Button>
            </div>
            <Table
                loading={loading}
                columns={columns}
                dataSource={details}
                scroll={{ x: "max-content" }}
                rowKey={(record) => record.id}
                onChange={handleTableChange}
                pagination={false}
            />
            {isModalOpen &&
                <Modal
                    title="Android App Version"
                    open={isModalOpen}
                    onCancel={() => {
                        reset();
                        setIsModalOpen(false)
                    }}
                    footer={null}
                    width={600}
                >
                    <form onSubmit={handleSubmit(onUpdateForm)}>
                        <div className="mb-3">
                            <label className="form-label">
                                Version Number <span className="text-danger">*</span>
                            </label>
                            <Controller
                                name="version_number"
                                control={control}
                                render={({ field }) => <Input {...field} />}
                            />
                            {errors.name && (
                                <div className="text-danger small">{errors.name.message}</div>
                            )}
                        </div>
                        <div className="mb-3">
                            <label className="form-label">
                                Force Update <span className="text-danger">*</span>
                            </label>
                            <Controller
                                name="is_force_update"
                                control={control}
                                render={({ field }) =>
                                    <Select
                                        {...field}
                                        showSearch
                                        placeholder="Select Force Update"
                                        filterOption={(input, option) => {
                                            return (option?.children ?? "")
                                                .toLowerCase()
                                                .includes(input.toLowerCase());
                                        }}
                                        style={{ width: "100%" }}
                                        onChange={field.onChange}
                                    >
                                        {isForceUpdate.map((update) => (
                                            <Select.Option value={update.value} key={update.value}>
                                                {update.label}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                }
                            />
                            {errors.name && (
                                <div className="text-danger small">{errors.name.message}</div>
                            )}
                        </div>
                        <div className="d-flex justify-content-end">
                            <Button type="primary" htmlType="submit" disabled={loading} loading={loading}>
                                Update Version
                            </Button>
                        </div>
                    </form>
                </Modal>
            }
            <Modal
                title="Add Android App Version"
                open={addModalOpen}
                onCancel={() => setAddModalOpen(false)}
                onOk={handleAdd}
                okText="Add"
                okButtonProps={{ disabled: !newVersion.trim() }}
            >
                <div className="mb-3">
                    <label className="form-label">Version Number</label>
                    <Input
                        value={newVersion}
                        onChange={(e) => setNewVersion(e.target.value)}
                        placeholder="e.g. 1.2.3"
                    />
                </div>
                <div className="mb-3">
                    <label className="form-label">Force Update</label>
                    <Select
                        value={newIsForceUpdate}
                        style={{ width: "100%" }}
                        onChange={(value) => setNewIsForceUpdate(value)}
                    >
                        {isForceUpdate.map((update) => (
                            <Select.Option value={update.value} key={update.value}>
                                {update.label}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
            </Modal>
        </div>
        </RequirePermission>
    )
}

export default AndroidAppVersions;
